/**
 * RecurrenceEditor — Meeting-style event scheduler + optional recurrence.
 *
 * Section 1 — Event template (always visible):
 *   All-day toggle · Start date + time · End date + time · Duration callout
 *
 * Section 2 — Recurrence (toggled on/off):
 *   Every [N] [Weekly · Monthly · Yearly]
 *   Weekly  → day-of-week chip selector
 *   Monthly → nth day note + optional "last day of month" checkbox
 *   Yearly  → summary note (Feb 29 caveat)
 *   Ends: Never · On date · After N occurrences
 *
 * Section 3 — Summary sentence
 *
 * Timezone is the tenant's; it is carried in the value but not shown in this form.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type RecurrenceGranularity = "minutely" | "hourly" | "daily" | "weekly" | "monthly" | "yearly";

export type RecurrenceValue = {
  all_day: boolean;
  start_date: string;         // "YYYY-MM-DD"
  start_time: string;         // "HH:MM" (ignored when all_day)
  end_date: string;           // "YYYY-MM-DD"
  end_time: string;           // "HH:MM" (ignored when all_day)
  is_recurring: boolean;
  granularity: RecurrenceGranularity;
  interval: number;           // every N periods
  byday: string[];            // ["MO","SA"] — weekly day filter
  monthly_last_day: boolean;  // monthly: always use last day of month
  end_mode: "none" | "on_date" | "after_n";
  end_date_recur: string;     // "YYYY-MM-DD" — when "on_date"
  end_count: number;          // when "after_n"
  timezone: string;           // IANA; tenant-set, not shown in UI
};

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, (d ?? 1) + n));
  return dt.toISOString().slice(0, 10);
}

export const EMPTY_RECURRENCE: RecurrenceValue = {
  all_day: false,
  start_date: today(),
  start_time: "22:00",
  end_date: addDays(today(), 1),
  end_time: "06:00",
  is_recurring: true,
  granularity: "weekly",
  interval: 1,
  byday: ["SA", "SU"],
  monthly_last_day: false,
  end_mode: "none",
  end_date_recur: "",
  end_count: 12,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC",
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const WEEK_DAYS: { code: string; label: string }[] = [
  { code: "MO", label: "Mon" },
  { code: "TU", label: "Tue" },
  { code: "WE", label: "Wed" },
  { code: "TH", label: "Thu" },
  { code: "FR", label: "Fri" },
  { code: "SA", label: "Sat" },
  { code: "SU", label: "Sun" },
];

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function parseHHMM(t: string): number {
  const [h = "0", m = "0"] = t.split(":");
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

function fmt12(hhmm: string): string {
  const mins = parseHHMM(hhmm);
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function domFromDate(dateStr: string): number {
  return parseInt(dateStr.split("-")[2] ?? "1", 10);
}

function monthIdxFromDate(dateStr: string): number {
  return parseInt(dateStr.split("-")[1] ?? "1", 10) - 1;
}

function fmtDate(dateStr: string): string {
  if (!dateStr) return "—";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, d ?? 1));
  return dt.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function isLastDayOfMonth(dateStr: string): boolean {
  if (!dateStr) return false;
  const [y, m, d] = dateStr.split("-").map(Number);
  // Noon UTC avoids DST/offset edge cases
  const next = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, (d ?? 1) + 1, 12, 0, 0));
  return next.getUTCDate() === 1;
}

const DAY_CODES_SUN_FIRST = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;
const DAY_FULL_NAMES: Record<string, string> = {
  SU: "Sunday", MO: "Monday", TU: "Tuesday", WE: "Wednesday",
  TH: "Thursday", FR: "Friday", SA: "Saturday",
};

/** Returns the two-letter weekday code (SU..SA) for a YYYY-MM-DD local date. */
function dayCodeFromDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, d ?? 1, 12, 0, 0));
  return DAY_CODES_SUN_FIRST[dt.getUTCDay()] ?? "MO";
}

/** Total duration in minutes across date+time boundaries. */
function spanMinutes(sd: string, st: string, ed: string, et: string): number {
  const [sy, sm, sd_] = sd.split("-").map(Number);
  const [ey, em, ed_] = ed.split("-").map(Number);
  const dayDiff =
    (Date.UTC(ey ?? 2000, (em ?? 1) - 1, ed_ ?? 1) -
     Date.UTC(sy ?? 2000, (sm ?? 1) - 1, sd_ ?? 1)) /
    86400000;
  return dayDiff * 1440 + parseHHMM(et) - parseHHMM(st);
}

function fmtDuration(mins: number): string {
  if (mins <= 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

function dayListSentence(byday: string[]): string {
  const full: Record<string, string> = {
    MO: "Monday", TU: "Tuesday", WE: "Wednesday", TH: "Thursday",
    FR: "Friday", SA: "Saturday", SU: "Sunday",
  };
  const names = byday.map((c) => full[c] ?? c);
  if (names.length === 0) return "no days selected";
  if (names.length === 1) return names[0]!;
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}

function granularitySingular(g: RecurrenceGranularity): string {
  switch (g) {
    case "minutely": return "minute";
    case "hourly": return "hour";
    case "daily": return "day";
    case "weekly": return "week";
    case "monthly": return "month";
    case "yearly": return "year";
  }
}
function granularityPlural(g: RecurrenceGranularity): string {
  return granularitySingular(g) + "s";
}

const GRANULARITY_LABEL: Record<RecurrenceGranularity, string> = {
  minutely: "Minutely",
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
};

// ---------------------------------------------------------------------------
// Summary sentence
// ---------------------------------------------------------------------------

function buildSummary(v: RecurrenceValue): string {
  const isMulti = v.end_date > v.start_date;
  const durationMins = spanMinutes(v.start_date, v.start_time, v.end_date, v.end_time);
  const durationStr = fmtDuration(durationMins);
  // Multi-day windows that span more than 24h are anchored to specific
  // weekdays (e.g. "Friday 5:00 PM to Monday 6:00 AM"). Shorter overnight
  // windows recur on whatever days the user picks, so we say "5:00 PM to
  // 6:00 AM the next day" without naming the start weekday.
  const longSpan = !v.all_day && durationMins > 1440;
  const timeRange = v.all_day
    ? "all day"
    : longSpan
    ? `${DAY_FULL_NAMES[dayCodeFromDate(v.start_date)] ?? ""} ${fmt12(v.start_time)} to ${DAY_FULL_NAMES[dayCodeFromDate(v.end_date)] ?? ""} ${fmt12(v.end_time)} (${durationStr})`
    : isMulti
    ? `${fmt12(v.start_time)} to ${fmt12(v.end_time)} the next day (${durationStr})`
    : `${fmt12(v.start_time)} to ${fmt12(v.end_time)} (${durationStr})`;

  if (!v.is_recurring) {
    if (v.all_day) {
      return v.start_date === v.end_date
        ? `Blackout applies on ${fmtDate(v.start_date)}, all day.`
        : `Blackout applies from ${fmtDate(v.start_date)} through ${fmtDate(v.end_date)}, all day.`;
    }
    return v.start_date === v.end_date
      ? `Blackout applies on ${fmtDate(v.start_date)}, ${timeRange}.`
      : `Blackout applies from ${fmtDate(v.start_date)} through ${fmtDate(v.end_date)}, ${timeRange}.`;
  }

  const startDesc = `starting ${fmtDate(v.start_date)}`;
  const endDesc =
    v.end_mode === "on_date" && v.end_date_recur
      ? `through ${fmtDate(v.end_date_recur)}`
      : v.end_mode === "after_n"
      ? `for ${v.end_count} occurrence${v.end_count !== 1 ? "s" : ""}`
      : "with no end date";

  const n = v.interval;
  const period = n === 1
    ? `every ${granularitySingular(v.granularity)}`
    : `every ${n} ${granularityPlural(v.granularity)}`;

  switch (v.granularity) {
    case "minutely":
      return n === 1
        ? `Blackout applies every minute, ${endDesc}.`
        : `Blackout applies every ${n} minutes, ${endDesc}.`;
    case "hourly":
      return n === 1
        ? `Blackout applies every hour at the top of the hour, ${endDesc}.`
        : `Blackout applies every ${n} hours at the top of the hour, ${endDesc}.`;
    case "daily": {
      if (v.byday.length === 0) return "Select at least one day of the week.";
      const allDays = v.byday.length === 7;
      const dayDesc = allDays ? "every day" : `on ${dayListSentence(v.byday)}`;
      return `Blackout applies ${dayDesc}, ${timeRange}, ${startDesc}, ${endDesc}.`;
    }
    case "weekly": {
      // Anchored to start_date's weekday — no chip list.
      const dayName = DAY_FULL_NAMES[dayCodeFromDate(v.start_date)] ?? "";
      const desc = n === 1 ? `every ${dayName}` : `every ${n} weeks on ${dayName}`;
      if (longSpan) {
        // timeRange already names start + end weekdays — fold into the desc.
        return `Blackout applies ${desc} ${fmt12(v.start_time)} through ${DAY_FULL_NAMES[dayCodeFromDate(v.end_date)] ?? ""} ${fmt12(v.end_time)} (${durationStr}), ${startDesc}, ${endDesc}.`;
      }
      return `Blackout applies ${desc}, ${timeRange}, ${startDesc}, ${endDesc}.`;
    }
    case "monthly": {
      const dom = domFromDate(v.start_date);
      const byDesc = v.monthly_last_day
        ? "the last day of the month"
        : dom > 28
        ? `day ${dom} of the month (or last day for shorter months)`
        : `day ${dom} of the month`;
      return `Blackout applies ${period} on ${byDesc}, ${timeRange}, ${startDesc}, ${endDesc}.`;
    }
    case "yearly": {
      const dom = domFromDate(v.start_date);
      const monthName = MONTH_NAMES[monthIdxFromDate(v.start_date)] ?? "";
      const isFeb29 = monthIdxFromDate(v.start_date) === 1 && dom === 29;
      const dateDesc = isFeb29
        ? "February 28/29 (Feb 28 in non-leap years)"
        : `${monthName} ${dom}`;
      return `Blackout applies ${period} on ${dateDesc}, ${timeRange}, ${startDesc}, ${endDesc}.`;
    }
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Radio({ name, value, checked, label, onChange }: {
  name: string; value: string; checked: boolean; label: string; onChange: () => void;
}) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "3px 0", fontSize: 13 }}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        style={{ cursor: "pointer", accentColor: "var(--accent)" }}
      />
      {label}
    </label>
  );
}

function DayChip({ code, label, active, onClick }: {
  code: string; label: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={active ? `${label}: blocked` : `${label}: not blocked`}
      style={{
        minWidth: 44,
        height: 36,
        borderRadius: 6,
        border: `1.5px solid ${active ? "var(--danger-text, #c0392b)" : "var(--border)"}`,
        background: active ? "var(--danger-text, #c0392b)" : "var(--surface)",
        color: active ? "#fff" : "var(--muted)",
        cursor: "pointer",
        fontWeight: active ? 700 : 400,
        fontSize: 12,
        transition: "all 120ms ease",
        userSelect: "none",
      }}
    >
      {label}
    </button>
  );
}

function Divider() {
  return <div style={{ borderTop: "1px solid var(--border)", margin: "4px 0" }} />;
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export type RecurrenceEditorMode = "blackout" | "cron";

export function RecurrenceEditor({ value: v, onChange, mode = "blackout" }: {
  value: RecurrenceValue;
  onChange: (next: RecurrenceValue) => void;
  mode?: RecurrenceEditorMode;
}) {
  function set(patch: Partial<RecurrenceValue>) {
    onChange({ ...v, ...patch });
  }
  const isCron = mode === "cron";

  function handleGranularityChange(g: RecurrenceGranularity) {
    // When switching to monthly, pre-check last-day if start date is end of month
    set({
      granularity: g,
      ...(g === "monthly" ? { monthly_last_day: isLastDayOfMonth(v.start_date) } : {}),
    });
  }

  const dom = domFromDate(v.start_date);
  const startIsLastDayOfMonth = isLastDayOfMonth(v.start_date);
  const multiDay = v.end_date > v.start_date;
  const durationMins = v.all_day ? 0 : spanMinutes(v.start_date, v.start_time, v.end_date, v.end_time);
  const durationOk = durationMins > 0;

  // "Interval" granularities (minutely / hourly) fire on a fixed cycle and
  // don't carry a start_date / start_time — only the interval matters.
  const isInterval = v.granularity === "minutely" || v.granularity === "hourly";

  // Weekly is anchored to start_date's weekday (no chip list).
  // Daily shows chips that filter which weekdays the recurrence fires on.
  const startDow = dayCodeFromDate(v.start_date);

  const summary = buildSummary(v);

  return (
    <div style={{ display: "grid", gap: 14 }}>

      {/* ── All day ── (blackout-only) */}
      {!isCron && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={v.all_day}
            onChange={(e) => set({ all_day: e.target.checked })}
            style={{ width: 15, height: 15, cursor: "pointer", accentColor: "var(--accent)" }}
          />
          <span style={{ fontWeight: 500 }}>All day</span>
        </label>
      )}

      {/* ── Start row ── */}
      <div style={{ display: "grid", gridTemplateColumns: v.all_day ? "1fr" : "1fr 1fr", gap: 10 }}>
        <label className="field">
          <span className="label">Start date</span>
          <input
            className="input"
            type="date"
            value={v.start_date}
            onChange={(e) => set({ start_date: e.target.value })}
          />
        </label>
        {!v.all_day && (
          <label className="field">
            <span className="label">Start time</span>
            <input
              className="input"
              type="time"
              value={v.start_time}
              onChange={(e) => set({ start_time: e.target.value })}
            />
          </label>
        )}
      </div>

      {/* ── End row ── (blackout-only — cron schedules are point-in-time fires) */}
      {!isCron && (
        <div style={{ display: "grid", gridTemplateColumns: v.all_day ? "1fr" : "1fr 1fr", gap: 10 }}>
          <label className="field">
            <span className="label">End date</span>
            <input
              className="input"
              type="date"
              value={v.end_date}
              min={v.start_date}
              onChange={(e) => set({ end_date: e.target.value })}
            />
          </label>
          {!v.all_day && (
            <label className="field">
              <span className="label">End time</span>
              <input
                className="input"
                type="time"
                value={v.end_time}
                onChange={(e) => set({ end_time: e.target.value })}
              />
            </label>
          )}
        </div>
      )}

      {/* ── Duration callout ── (blackout-only) */}
      {!isCron && !v.all_day && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          borderRadius: 6,
          background: multiDay ? "var(--warn-bg, #fffbea)" : "var(--surface-2, var(--surface))",
          border: "1px solid var(--border)",
          fontSize: 12,
          color: "var(--muted)",
        }}>
          <span>{multiDay ? "🌙" : "⏱"}</span>
          <span>
            {fmt12(v.start_time)} → {fmt12(v.end_time)}
            {multiDay ? ` · ends ${fmtDate(v.end_date)}` : " · same day"}
            {durationOk && (
              <> — <strong style={{ color: "var(--text, inherit)" }}>{fmtDuration(durationMins)}</strong></>
            )}
          </span>
        </div>
      )}

      <Divider />

      {/* ── Recurring toggle ── (blackout-only — cron is always recurring) */}
      {!isCron && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={v.is_recurring}
            onChange={(e) => set({ is_recurring: e.target.checked })}
            style={{ width: 15, height: 15, cursor: "pointer", accentColor: "var(--accent)" }}
          />
          <span style={{ fontWeight: 500 }}>Repeats</span>
        </label>
      )}

      {/* ── Recurrence body ── */}
      {(isCron || v.is_recurring) && (
        <>
          {/* Every N [granularity] */}
          <div className="field">
            <span className="label">Every</span>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <input
                className="input"
                type="number"
                min={1}
                max={999}
                value={v.interval}
                onChange={(e) => set({ interval: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                style={{ width: 72 }}
              />
              {(["minutely", "hourly", "daily", "weekly", "monthly", "yearly"] as RecurrenceGranularity[]).map((g) => (
                <label key={g} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 13 }}>
                  <input
                    type="radio"
                    name="bw-gran"
                    value={g}
                    checked={v.granularity === g}
                    onChange={() => handleGranularityChange(g)}
                    style={{ cursor: "pointer", accentColor: "var(--accent)" }}
                  />
                  {GRANULARITY_LABEL[g]}
                </label>
              ))}
            </div>
          </div>

          {/* Daily: day-of-week chips filter which weekdays the recurrence fires on */}
          {v.granularity === "daily" && (
            <div className="field">
              <span className="label">On</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                {WEEK_DAYS.map((day) => (
                  <DayChip
                    key={day.code}
                    code={day.code}
                    label={day.label}
                    active={v.byday.includes(day.code)}
                    onClick={() => {
                      const next = v.byday.includes(day.code)
                        ? v.byday.filter((d) => d !== day.code)
                        : [...v.byday, day.code];
                      set({ byday: next });
                    }}
                  />
                ))}
              </div>
              {v.byday.length === 0 && (
                <div className="help" style={{ color: "var(--danger-text)", marginTop: 4 }}>
                  Select at least one day.
                </div>
              )}
            </div>
          )}

          {/* Weekly: anchored to start_date's weekday — no chip list */}
          {v.granularity === "weekly" && (
            <div className="field">
              <span className="label">On</span>
              <div style={{ display: "grid", gap: 4, marginTop: 2 }}>
                <span style={{ fontSize: 13 }}>
                  Repeats every <strong>{DAY_FULL_NAMES[startDow] ?? startDow}</strong>
                </span>
                <span className="help">
                  The weekday is set by the start date. Change the start date to pick a different weekday.
                </span>
              </div>
            </div>
          )}

          {/* Monthly: nth-day note + optional last-day checkbox */}
          {v.granularity === "monthly" && (
            <div className="field">
              <span className="label">On</span>
              <div style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 13 }}>Day {dom} of the month</span>
                {startIsLastDayOfMonth && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={v.monthly_last_day}
                      onChange={(e) => set({ monthly_last_day: e.target.checked })}
                      style={{ cursor: "pointer", accentColor: "var(--accent)" }}
                    />
                    Last day of the month
                    <span className="help" style={{ marginLeft: 4 }}>
                      (varies: Jan 31, Feb 28/29, Apr 30…)
                    </span>
                  </label>
                )}
                {dom > 28 && !v.monthly_last_day && (
                  <div className="help">
                    In months with fewer than {dom} days, blackout falls on the last day of that month.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Yearly: summary note */}
          {v.granularity === "yearly" && (
            <div className="help">
              Repeats on {MONTH_NAMES[monthIdxFromDate(v.start_date)] ?? ""} {dom} every{" "}
              {v.interval === 1 ? "year" : `${v.interval} years`}.
              {monthIdxFromDate(v.start_date) === 1 && dom === 29
                ? " Feb 29 falls back to Feb 28 in non-leap years."
                : null}
            </div>
          )}

          {/* Recurrence end — blackout-only; cron is always open-ended */}
          {!isCron && (
            <>
              <Divider />
              <div className="field">
                <span className="label">Ends</span>
                <div style={{ display: "grid", gap: 2 }}>
                  <Radio name="bw-end" value="none"    checked={v.end_mode === "none"}    label="Never"               onChange={() => set({ end_mode: "none" })} />
                  <Radio name="bw-end" value="on_date" checked={v.end_mode === "on_date"} label="On date"             onChange={() => set({ end_mode: "on_date" })} />
                  <Radio name="bw-end" value="after_n" checked={v.end_mode === "after_n"} label="After N occurrences" onChange={() => set({ end_mode: "after_n" })} />
                </div>
              </div>

              {v.end_mode === "on_date" && (
                <label className="field">
                  <span className="label">End date</span>
                  <input
                    className="input"
                    type="date"
                    value={v.end_date_recur}
                    min={v.start_date}
                    onChange={(e) => set({ end_date_recur: e.target.value })}
                    style={{ maxWidth: 200 }}
                  />
                </label>
              )}

              {v.end_mode === "after_n" && (
                <label className="field">
                  <span className="label">Occurrences</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={9999}
                      value={v.end_count}
                      onChange={(e) => set({ end_count: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                      style={{ width: 100 }}
                    />
                    <span className="help">occurrence{v.end_count !== 1 ? "s" : ""}</span>
                  </div>
                </label>
              )}
            </>
          )}
        </>
      )}

      {/* ── Summary sentence ── (blackout-only — cron uses cronstrue elsewhere) */}
      {!isCron && (
        <div style={{
          padding: "10px 14px",
          borderRadius: 8,
          background: "var(--surface-2, var(--surface))",
          border: "1px solid var(--border)",
          fontSize: 13,
          lineHeight: 1.5,
        }}>
          <span style={{ fontSize: 16, marginRight: 6 }}>💡</span>
          {summary}
        </div>
      )}

      {/* ── DST help text ── (cron-only) */}
      {isCron && (
        <div className="help">
          Schedules fire at local time in {v.timezone}. On daylight-saving spring-forward the skipped hour is not fired; on fall-back the repeated hour fires once.
        </div>
      )}
    </div>
  );
}
